<script setup>
	import { onBeforeUnmount, reactive, watch } from "vue";
	import { executeServiceCall, getValidationErrorsFromResponse, selfServiceService } from "@/services";
	import largeLucentaryLogoUrl from "@/assets/img/lucentary-banner-blue.svg";

	const featuredQuotes = [
		{
			author: "Josh S",
			role: "CTO",
			quote: "Easily the smartest and most innovative engineer I have worked with. He fundamentally understands how to build robust systems that provide real value to clients.",
		},
		{
			author: "Raul G",
			role: "Product Lead",
			quote: "We hired Chad for a three-week turnaround and he delivered exceptionally. The customers felt it was professionally done and it exceeded expectations.",
		},
		{
			author: "Geoff A",
			role: "Engineering Leader",
			quote: "A wealth of experience and ability on every project he touches. An effective communicator, and a phenomenal mentor and teacher.",
		},
	];

	const transformations = [
		{
			before: "Breaks on every deploy",
			after: "Stable, tested, predictable",
		},
		{
			before: "Nobody understands the code",
			after: "Documented and yours",
		},
		{
			before: "Written by an AI in a hurry",
			after: "Hardened by senior engineers",
		},
		{
			before: "Too risky to build on",
			after: "Ready to scale",
		},
	];

	const data = reactive({
		requestMoreInfo: {
			contactInfo: {
				emailAddress: "",
				name: "",
				text: "",
			},
			isError: false,
			isProcessing: false,
			isShowing: false,
			isSuccess: false,
			topic: "",
			validationErrors: [],
		},
	});

	function openConsultation() {
		data.requestMoreInfo.topic = "Free consultation";
		data.requestMoreInfo.isError = false;
		data.requestMoreInfo.isSuccess = false;
		data.requestMoreInfo.isProcessing = false;
		data.requestMoreInfo.validationErrors = [];
		data.requestMoreInfo.isShowing = true;
	}

	function closeConsultation() {
		data.requestMoreInfo.isShowing = false;
	}

	async function submitConsultation() {
		data.requestMoreInfo.isProcessing = true;
		data.requestMoreInfo.isError = false;
		data.requestMoreInfo.validationErrors = [];
		data.requestMoreInfo.contactInfo.phoneNumber = "n/a";
		data.requestMoreInfo.contactInfo.recaptchaResponse = "skip";

		await executeServiceCall(() => selfServiceService.public.submitContactRequest(data.requestMoreInfo.contactInfo))
			.then(() => {
				data.requestMoreInfo.isSuccess = true;
			})
			.catch(error => {
				if (error?.response?.status === 400) {
					data.requestMoreInfo.validationErrors = getValidationErrorsFromResponse(error.response).map(validationMessage =>
						validationMessage === "Your E-mail Address is required."
							? "Your E-mail Address or Phone Number is required."
							: validationMessage
					);
				} else {
					console.error(error);
					data.requestMoreInfo.isError = true;
				}
			})
			.finally(() => {
				data.requestMoreInfo.isProcessing = false;
			});
	}

	function handleModalKeydown(event) {
		if (event.key === "Escape") {
			closeConsultation();
		}
	}

	watch(() => data.requestMoreInfo.isShowing, (isShowing) => {
		if (isShowing) {
			const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
			document.documentElement.style.overflow = "hidden";
			document.body.style.overflow = "hidden";

			if (scrollbarWidth > 0) {
				document.body.style.paddingRight = `${scrollbarWidth}px`;
			}

			window.addEventListener("keydown", handleModalKeydown);
		} else {
			document.documentElement.style.overflow = "";
			document.body.style.overflow = "";
			document.body.style.paddingRight = "";
			window.removeEventListener("keydown", handleModalKeydown);
		}
	});

	onBeforeUnmount(() => {
		document.documentElement.style.overflow = "";
		document.body.style.overflow = "";
		document.body.style.paddingRight = "";
		window.removeEventListener("keydown", handleModalKeydown);
	});
</script>

<template>
	<div class="promo-b">
		<header class="site-header">
			<div class="site-header-inner">
				<span class="brand">
					<img class="brand-logo" :src="largeLucentaryLogoUrl" alt="Lucentary" />
				</span>
				<button type="button" class="btn btn-primary btn-compact" @click="openConsultation">Book a call</button>
			</div>
		</header>

		<section class="hero">
			<div class="hero-inner">
				<span class="eyebrow">Software rescue, led by Chad Greer</span>
				<h1 class="hero-title">Stop duct-taping your codebase.</h1>
				<p class="hero-lead">
					You shipped fast with AI. Now it's fragile, undocumented, and one deploy from
					disaster. We quietly fix it — and hand you back software you can finally trust.
				</p>
				<div class="hero-actions">
					<button type="button" class="btn btn-primary btn-large" @click="openConsultation">Book your free consultation</button>
				</div>
				<div class="hero-trust">
					<span class="hero-stars" aria-hidden="true">★★★★★</span>
					<span>Trusted by founders &amp; CTOs · 20+ years shipping software · Senior engineers, not juniors</span>
				</div>
			</div>
		</section>

		<section class="statement">
			<div class="statement-inner">
				<p class="statement-text">
					Most "fixes" just add another layer of tape. We do the opposite — we find what's
					actually broken, rebuild it properly, and make sure it stays fixed.
				</p>
			</div>
		</section>

		<section class="transform">
			<div class="transform-inner">
				<header class="block-header">
					<span class="eyebrow eyebrow-light">What changes</span>
					<h2 class="block-heading">From liability to leverage.</h2>
				</header>
				<ul class="transform-list">
					<li v-for="transformation in transformations" :key="transformation.before" class="transform-row">
						<span class="transform-before">
							<span class="transform-tag transform-tag-before">Before</span>
							{{ transformation.before }}
						</span>
						<span class="transform-arrow" aria-hidden="true">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
						</span>
						<span class="transform-after">
							<span class="transform-tag transform-tag-after">After</span>
							{{ transformation.after }}
						</span>
					</li>
				</ul>
			</div>
		</section>

		<section class="quotes">
			<div class="quotes-inner">
				<header class="block-header">
					<span class="eyebrow">Don't take our word for it</span>
					<h2 class="block-heading">What people say about Chad.</h2>
				</header>
				<div class="quotes-grid">
					<figure v-for="featuredQuote in featuredQuotes" :key="featuredQuote.author" class="quote-card">
						<div class="quote-mark" aria-hidden="true">&ldquo;</div>
						<blockquote class="quote-text">{{ featuredQuote.quote }}</blockquote>
						<figcaption class="quote-author">
							<span class="quote-name">{{ featuredQuote.author }}</span>
							<span class="quote-role">{{ featuredQuote.role }}</span>
						</figcaption>
					</figure>
				</div>
			</div>
		</section>

		<section class="promise">
			<div class="promise-inner">
				<h2 class="promise-title">The call is free. The honesty is the point.</h2>
				<p class="promise-text">
					Thirty minutes with Chad. You'll leave knowing exactly what's wrong and what it takes
					to fix it — even if you never hire us. If it's not worth saving, we'll tell you.
				</p>
				<button type="button" class="btn btn-light btn-large" @click="openConsultation">Book your free consultation</button>
				<p class="promise-note">New clients get 50% off their first 6 weeks · Limited spots each month</p>
			</div>
		</section>

		<section class="final-cta">
			<div class="final-cta-inner">
				<h2 class="final-cta-title">Your software should be an asset, not an anxiety.</h2>
				<button type="button" class="btn btn-primary btn-large" @click="openConsultation">Book your free consultation</button>
				<p class="final-cta-note">Free · 30 minutes · No obligation</p>
			</div>
		</section>

		<footer class="site-footer">
			<div class="site-footer-inner">
				<span class="brand">
					<img class="brand-logo" :src="largeLucentaryLogoUrl" alt="Lucentary" />
				</span>
				<p class="footer-copy">Professional software services. &copy; {{ new Date().getFullYear() }} Lucentary.</p>
			</div>
		</footer>

		<transition name="modal-fade">
			<div v-if="data.requestMoreInfo.isShowing" class="modal-overlay" @click.self="closeConsultation">
				<div class="modal-dialog" role="dialog" aria-modal="true" aria-labelledby="consultationTitle" @click.stop>
					<button type="button" class="modal-close" aria-label="Close" @click="closeConsultation">&times;</button>
					<div class="modal-scroll">
					<template v-if="!data.requestMoreInfo.isSuccess">
					<span class="modal-badge">Free · 30 minutes · No obligation</span>
					<h2 id="consultationTitle" class="modal-title">Book your free consultation</h2>
					<p class="modal-description">Tell us a little about your situation and Chad will be in touch to schedule your call.</p>
					<div class="modal-field">
						<label for="consultationName">Name</label>
						<input id="consultationName" v-model="data.requestMoreInfo.contactInfo.name" type="text" autocomplete="name" :disabled="data.requestMoreInfo.isProcessing" />
					</div>
					<div class="modal-field">
						<label for="consultationEmail">Email or phone number</label>
						<input id="consultationEmail" v-model="data.requestMoreInfo.contactInfo.emailAddress" type="text" autocomplete="email" :disabled="data.requestMoreInfo.isProcessing" />
					</div>
					<div class="modal-field">
						<label for="consultationText">What's going on with your software?</label>
						<textarea id="consultationText" v-model="data.requestMoreInfo.contactInfo.text" rows="4" :disabled="data.requestMoreInfo.isProcessing"></textarea>
					</div>
					<div v-if="data.requestMoreInfo.validationErrors.length > 0" class="modal-feedback modal-feedback-error" role="alert">
						<p class="modal-feedback-title">Please fix the following:</p>
						<ul class="modal-feedback-list">
							<li v-for="(validationError, validationErrorIndex) in data.requestMoreInfo.validationErrors" :key="validationErrorIndex">{{ validationError }}</li>
						</ul>
					</div>
					<p v-else-if="data.requestMoreInfo.isError" class="modal-feedback modal-feedback-error" role="alert">Something went wrong sending your request. Please try again.</p>
					<button type="button" class="btn btn-primary btn-block" :disabled="data.requestMoreInfo.isProcessing" @click="submitConsultation"><span v-if="data.requestMoreInfo.isProcessing" class="btn-spinner" aria-hidden="true"></span>{{ data.requestMoreInfo.isProcessing ? "Sending…" : "Request my free call" }}</button>
					</template>
					<div v-else class="modal-success">
						<span class="success-check" aria-hidden="true">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
						</span>
						<h2 class="modal-title">Request received</h2>
						<p class="modal-description">Thanks for reaching out — Chad Greer will get in touch with you shortly to schedule your free consultation.</p>
					</div>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<style scoped>
	.promo-b {
		--bg: #ffffff;
		--bg-tint: #f4f7fe;
		--ink: #0c1a33;
		--ink-soft: #45556f;
		--ink-muted: #6e7e98;
		--blue: #2563eb;
		--blue-deep: #1d4ed8;
		--blue-soft: #e0eafd;
		--border: #e6edf8;
		--border-strong: #cdd9ec;
		--navy: #0a1730;

		font-family: "Poppins", "Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
		color: var(--ink);
		background: var(--bg);
		overflow-x: hidden;
	}

	.site-header {
		position: sticky;
		top: 0;
		z-index: 900;
		background: rgba(255, 255, 255, 0.85);
		backdrop-filter: blur(12px);
		-webkit-backdrop-filter: blur(12px);
		border-bottom: 1px solid var(--border);
	}

	.site-header-inner {
		max-width: 1120px;
		margin: 0 auto;
		padding: 0.85rem 1.5rem;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
	}

	.brand {
		display: inline-flex;
		align-items: center;
		gap: 0.6rem;
		text-decoration: none;
		color: var(--navy);
	}

	.brand-logo {
		height: 96px;
		width: auto;
		display: block;
	}

	.btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.95rem 1.8rem;
		border-radius: 999px;
		font-family: inherit;
		font-size: 1.05rem;
		font-weight: 600;
		text-decoration: none;
		cursor: pointer;
		border: 2px solid transparent;
		transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
	}

	.btn-primary {
		background: var(--blue);
		color: #ffffff;
		box-shadow: 0 16px 34px -16px rgba(37, 99, 235, 0.8);
	}

	.btn-primary:hover {
		transform: translateY(-2px);
		background: var(--blue-deep);
		box-shadow: 0 22px 42px -18px rgba(37, 99, 235, 0.85);
	}

	.btn-primary:focus-visible {
		outline: 3px solid rgba(37, 99, 235, 0.4);
		outline-offset: 3px;
	}

	.btn-light {
		background: #ffffff;
		color: var(--blue-deep);
		box-shadow: 0 16px 34px -16px rgba(0, 0, 0, 0.45);
	}

	.btn-light:hover {
		transform: translateY(-2px);
		background: #eef4ff;
	}

	.btn-light:focus-visible {
		outline: 3px solid rgba(255, 255, 255, 0.7);
		outline-offset: 3px;
	}

	.btn-large {
		padding: 1.15rem 2.4rem;
		font-size: 1.18rem;
	}

	.btn-compact {
		padding: 0.65rem 1.4rem;
		font-size: 0.98rem;
	}

	.btn-block {
		width: 100%;
		border-radius: 14px;
	}

	.btn:disabled {
		opacity: 0.65;
		cursor: not-allowed;
		transform: none;
		box-shadow: none;
	}

	.btn-spinner {
		width: 1.05em;
		height: 1.05em;
		flex-shrink: 0;
		margin-right: 0.55rem;
		border: 2px solid rgba(255, 255, 255, 0.45);
		border-top-color: #ffffff;
		border-radius: 50%;
		animation: btn-spin 0.7s linear infinite;
	}

	@keyframes btn-spin {
		to {
			transform: rotate(360deg);
		}
	}

	.modal-success {
		text-align: center;
		padding: 0.75rem 0.25rem 0.5rem;
	}

	.success-check {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 72px;
		height: 72px;
		border-radius: 50%;
		background: #dcfce7;
		color: #16a34a;
		margin-bottom: 1.35rem;
	}

	.success-check svg {
		width: 36px;
		height: 36px;
	}

	.eyebrow {
		display: inline-block;
		font-size: 0.9rem;
		font-weight: 700;
		letter-spacing: 0.14em;
		text-transform: uppercase;
		color: var(--blue-deep);
		margin-bottom: 1.25rem;
	}

	.eyebrow-light {
		color: var(--sky, #9cc0fb);
	}

	.hero {
		padding: clamp(4rem, 9vw, 8rem) 1.5rem clamp(3.5rem, 7vw, 6rem);
		text-align: center;
	}

	.hero-inner {
		max-width: 880px;
		margin: 0 auto;
	}

	.hero-title {
		font-size: clamp(2.9rem, 7vw, 5.5rem);
		line-height: 1.0;
		font-weight: 800;
		letter-spacing: -0.03em;
		color: var(--navy);
		margin: 0 0 1.75rem;
	}

	.hero-lead {
		font-size: clamp(1.18rem, 1.7vw, 1.5rem);
		line-height: 1.6;
		color: var(--ink-soft);
		max-width: 60ch;
		margin: 0 auto 2.5rem;
	}

	.hero-actions {
		display: flex;
		justify-content: center;
		margin-bottom: 2rem;
	}

	.hero-trust {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 0.6rem;
		flex-wrap: wrap;
		font-size: 1rem;
		color: var(--ink-soft);
		max-width: 60ch;
		margin: 0 auto;
	}

	.hero-stars {
		color: #f5a623;
		letter-spacing: 0.05em;
	}

	.statement {
		padding: clamp(3rem, 6vw, 5rem) 1.5rem;
		background: var(--bg-tint);
	}

	.statement-inner {
		max-width: 920px;
		margin: 0 auto;
		text-align: center;
	}

	.statement-text {
		font-size: clamp(1.5rem, 3vw, 2.4rem);
		line-height: 1.3;
		font-weight: 600;
		letter-spacing: -0.01em;
		color: var(--navy);
		margin: 0;
	}

	.transform {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--navy);
		color: #ffffff;
	}

	.transform-inner {
		max-width: 920px;
		margin: 0 auto;
	}

	.block-header {
		text-align: center;
		margin-bottom: 3rem;
	}

	.block-heading {
		font-size: clamp(2rem, 3.6vw, 3.2rem);
		font-weight: 800;
		letter-spacing: -0.025em;
		line-height: 1.08;
		margin: 0;
	}

	.transform .block-heading {
		color: #ffffff;
	}

	.transform-list {
		list-style: none;
		margin: 0;
		padding: 0;
		display: grid;
		gap: 1rem;
	}

	.transform-row {
		display: grid;
		grid-template-columns: 1fr auto 1fr;
		align-items: center;
		gap: 1.25rem;
		padding: 1.25rem 1.5rem;
		border-radius: 16px;
		background: rgba(255, 255, 255, 0.05);
		border: 1px solid rgba(255, 255, 255, 0.1);
	}

	.transform-before,
	.transform-after {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		font-size: clamp(1.05rem, 1.5vw, 1.25rem);
		font-weight: 500;
	}

	.transform-before {
		color: rgba(255, 255, 255, 0.6);
	}

	.transform-after {
		color: #ffffff;
		font-weight: 600;
		text-align: right;
	}

	.transform-tag {
		font-size: 0.72rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
	}

	.transform-tag-before {
		color: rgba(255, 255, 255, 0.45);
	}

	.transform-tag-after {
		color: #7fb0ff;
	}

	.transform-arrow {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 42px;
		height: 42px;
		border-radius: 50%;
		background: var(--blue);
		color: #ffffff;
		flex-shrink: 0;
	}

	.transform-arrow svg {
		width: 20px;
		height: 20px;
	}

	.quotes {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--bg);
	}

	.quotes-inner {
		max-width: 1120px;
		margin: 0 auto;
	}

	.quotes .block-heading {
		color: var(--navy);
	}

	.quotes-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 1.5rem;
	}

	.quote-card {
		display: flex;
		flex-direction: column;
		margin: 0;
		padding: 2.25rem 2rem;
		border-radius: 20px;
		background: var(--bg-tint);
		border: 1px solid var(--border);
	}

	.quote-mark {
		font-size: 3.5rem;
		line-height: 0.5;
		color: var(--blue);
		margin-bottom: 1rem;
	}

	.quote-text {
		flex: 1;
		font-size: clamp(1.1rem, 1.5vw, 1.28rem);
		line-height: 1.5;
		font-weight: 500;
		color: var(--ink);
		margin: 0 0 1.5rem;
	}

	.quote-author {
		display: flex;
		flex-direction: column;
		gap: 0.15rem;
	}

	.quote-name {
		font-size: 1.02rem;
		font-weight: 700;
		color: var(--navy);
	}

	.quote-role {
		font-size: 0.9rem;
		color: var(--ink-muted);
	}

	.promise {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
	}

	.promise-inner {
		max-width: 860px;
		margin: 0 auto;
		text-align: center;
		padding: clamp(2.75rem, 5vw, 4.5rem);
		border-radius: 28px;
		background: linear-gradient(135deg, var(--blue) 0%, #1e40af 100%);
		color: #ffffff;
		box-shadow: 0 36px 72px -38px rgba(37, 99, 235, 0.7);
	}

	.promise-title {
		font-size: clamp(1.9rem, 3.4vw, 2.9rem);
		font-weight: 800;
		letter-spacing: -0.025em;
		line-height: 1.1;
		margin: 0 auto 1.25rem;
		max-width: 18ch;
		color: #ffffff;
	}

	.promise-text {
		font-size: clamp(1.08rem, 1.4vw, 1.25rem);
		line-height: 1.6;
		color: rgba(255, 255, 255, 0.92);
		max-width: 54ch;
		margin: 0 auto 2rem;
	}

	.promise-note {
		margin: 1.25rem 0 0;
		font-size: 0.95rem;
		color: rgba(255, 255, 255, 0.78);
	}

	.final-cta {
		padding: clamp(4rem, 8vw, 7rem) 1.5rem;
		background: var(--bg-tint);
		text-align: center;
	}

	.final-cta-inner {
		max-width: 760px;
		margin: 0 auto;
	}

	.final-cta-title {
		font-size: clamp(2.1rem, 4vw, 3.4rem);
		font-weight: 800;
		letter-spacing: -0.025em;
		line-height: 1.08;
		color: var(--navy);
		margin: 0 auto 2rem;
		max-width: 18ch;
	}

	.final-cta-note {
		margin: 1.25rem 0 0;
		font-size: 0.95rem;
		color: var(--ink-muted);
		letter-spacing: 0.02em;
	}

	.site-footer {
		padding: 2.5rem 1.5rem;
		background: var(--bg);
		border-top: 1px solid var(--border);
	}

	.site-footer-inner {
		max-width: 1120px;
		margin: 0 auto;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		flex-wrap: wrap;
	}

	.footer-copy {
		font-size: 0.92rem;
		color: var(--ink-muted);
		margin: 0;
	}

	.modal-overlay {
		position: fixed;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1.5rem;
		background: rgba(10, 23, 48, 0.55);
		backdrop-filter: blur(5px);
		-webkit-backdrop-filter: blur(5px);
		cursor: pointer;
		z-index: 100000;
	}

	.modal-dialog {
		position: relative;
		width: 100%;
		max-width: 520px;
		max-height: calc(100vh - 3rem);
		max-height: calc(100dvh - 3rem);
		overflow: hidden;
		pointer-events: auto;
		border-radius: 22px;
		background: var(--bg);
		box-shadow: 0 40px 90px -30px rgba(10, 23, 48, 0.6);
		cursor: default;
	}

	.modal-scroll {
		max-height: calc(100vh - 3rem);
		max-height: calc(100dvh - 3rem);
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
		padding: clamp(2.25rem, 4vw, 2.85rem) clamp(1.75rem, 3vw, 2.4rem);
	}

	.modal-close {
		position: absolute;
		top: 1.1rem;
		right: 1.25rem;
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1.9rem;
		line-height: 1;
		border-radius: 12px;
		border: none;
		background: var(--bg-tint);
		color: var(--ink-muted);
		cursor: pointer;
		transition: background 0.2s ease, color 0.2s ease;
	}

	.modal-close:hover {
		background: var(--blue-soft);
		color: var(--blue-deep);
	}

	.modal-badge {
		display: inline-block;
		padding: 0.4rem 0.9rem;
		border-radius: 999px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.85rem;
		font-weight: 700;
	}

	.modal-title {
		font-size: clamp(1.6rem, 2.4vw, 2rem);
		font-weight: 800;
		letter-spacing: -0.01em;
		color: var(--navy);
		margin: 0.75rem 0 0.6rem;
	}

	.modal-description {
		font-size: 1.02rem;
		line-height: 1.55;
		color: var(--ink-soft);
		margin: 0 0 1.75rem;
	}

	.modal-field {
		margin-bottom: 1.2rem;
		text-align: left;
	}

	.modal-field label {
		display: block;
		font-size: 1rem;
		font-weight: 600;
		color: var(--ink);
		margin-bottom: 0.5rem;
	}

	.modal-field input,
	.modal-field textarea {
		width: 100%;
		padding: 0.85rem 1rem;
		border-radius: 12px;
		border: 2px solid var(--border-strong);
		background: var(--bg-tint);
		font-family: inherit;
		font-size: 1.05rem;
		color: var(--ink);
		transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
		box-sizing: border-box;
	}

	.modal-field textarea {
		resize: vertical;
	}

	.modal-field input:focus,
	.modal-field textarea:focus {
		outline: none;
		background: var(--bg);
		border-color: var(--blue);
		box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
	}

	.modal-feedback {
		margin: 0 0 1.2rem;
		padding: 0.85rem 1rem;
		border-radius: 12px;
		font-size: 0.98rem;
		font-weight: 500;
		text-align: left;
	}

	.modal-feedback-error {
		background: #fbe9ec;
		color: #9c1f2e;
	}

	.modal-feedback-title {
		margin: 0 0 0.4rem;
		font-weight: 700;
	}

	.modal-feedback-list {
		margin: 0;
		padding-left: 1.2rem;
	}

	.modal-feedback-list li {
		margin-bottom: 0.2rem;
	}

	.toast {
		position: fixed;
		left: 50%;
		bottom: 2rem;
		transform: translateX(-50%);
		max-width: 92vw;
		padding: 1.05rem 1.6rem;
		border-radius: 14px;
		font-size: 1.02rem;
		font-weight: 600;
		box-shadow: 0 18px 40px -20px rgba(10, 23, 48, 0.5);
		z-index: 1100;
	}

	.toast-success {
		background: #0e7490;
		color: #ffffff;
	}

	.toast-error {
		background: #b4232c;
		color: #ffffff;
	}

	.modal-fade-enter-active,
	.modal-fade-leave-active {
		transition: opacity 0.25s ease;
	}

	.modal-fade-enter-active .modal-dialog,
	.modal-fade-leave-active .modal-dialog {
		transition: transform 0.25s ease, opacity 0.25s ease;
	}

	.modal-fade-enter-from,
	.modal-fade-leave-to {
		opacity: 0;
	}

	.modal-fade-enter-from .modal-dialog,
	.modal-fade-leave-to .modal-dialog {
		transform: translateY(14px) scale(0.98);
		opacity: 0;
	}

	.toast-fade-enter-active,
	.toast-fade-leave-active {
		transition: opacity 0.3s ease, transform 0.3s ease;
	}

	.toast-fade-enter-from,
	.toast-fade-leave-to {
		opacity: 0;
		transform: translateX(-50%) translateY(12px);
	}

	@media (max-width: 860px) {
		.quotes-grid {
			grid-template-columns: 1fr;
			max-width: 560px;
			margin: 0 auto;
		}
	}

	@media (max-width: 640px) {
		.brand-logo {
			height: 40px;
		}

		.modal-close {
			top: 0.9rem;
			right: 0.9rem;
			width: 36px;
			height: 36px;
			font-size: 1.7rem;
		}

		.modal-scroll {
			padding-top: 3.4rem;
		}

		.hero-actions {
			align-items: stretch;
		}

		.hero-actions .btn {
			width: 100%;
		}

		.transform-row {
			grid-template-columns: 1fr;
			gap: 1rem;
			text-align: left;
		}

		.transform-after {
			text-align: left;
		}

		.transform-arrow {
			transform: rotate(90deg);
		}

		.site-header .btn-compact {
			padding: 0.6rem 1.1rem;
			font-size: 0.9rem;
		}
	}
</style>